import React, { useEffect, useState } from "react";
import Stat from "../../components/Stat";
import DataTable from "../../components/DataTable";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import AiBehaviorHub from "./AiBehaviorHub";
import AdminTracker from "./AdminTracker";
import { errorMessage } from "../../services";
import { getAnalytics, getSessionActivities, getAiInsights } from "../../services/analytics";
import { money } from "../../utils/format";

const RANGES = [
  { value: "1h", label: "1 hr" },
  { value: "1d", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
];
const KIND_LABELS = { marketing: "Ownezzy site", demo: "Demo store", store: "Customer store" };
const shortDay = (day) => {
  if (!day) return "";
  const d = new Date(`${day}T00:00:00`);
  return isNaN(d) ? String(day) : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};
const count = (n) => Number(n || 0).toLocaleString("en-IN");
const percent = (part, whole) => (whole ? `${((part / whole) * 100).toFixed(1)}%` : "–");
const formatDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) return remS > 0 ? `${m}m ${remS}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
};
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleDateString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Visits, orders and sales of the websites this account can see.
// Super Admin: visitor IP and location tracking, conversion funnel, store activity stream (clicks, scrolls, nav),
// website purchases and sales, plus clicks on "Get it for free" on the demo stores.
// Store owner: their own stores, customer orders and sales.
function TrafficAnalytics({ user }) {
  const isSuper = user.role === "superadmin";
  const [range, setRange] = useState("30");
  const [site, setSite] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [activityFilter, setActivityFilter] = useState("all");
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [isRefreshingAi, setIsRefreshingAi] = useState(false);
  const [reAnalyzeToast, setReAnalyzeToast] = useState(null);

  const handleRefreshAi = () => {
    setIsRefreshingAi(true);
    setReAnalyzeToast(null);
    Promise.all([
      getAnalytics(range, site),
      getAiInsights(range, site),
    ])
      .then(([analyticsRes, aiRes]) => {
        const fullData = analyticsRes.data;
        if (aiRes.data?.insights) {
          fullData.tracking = {
            ...(fullData.tracking || {}),
            aiInsights: aiRes.data.insights,
          };
        }
        setData(fullData);
        setError("");
        setReAnalyzeToast({
          message: "All telemetry re-analyzed: Latest visitor sessions, clickstream interactions, and completed CRO optimizations refreshed.",
          timestamp: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        });
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setIsRefreshingAi(false));
  };

  useEffect(() => {
    let live = true;
    setData(null);
    getAnalytics(range, site)
      .then(({ data: result }) => { if (live) { setData(result); setError(""); } })
      .catch((err) => live && setError(errorMessage(err)));
    return () => { live = false; };
  }, [range, site]);

  const openSessionDetail = (s) => {
    setSelectedSession(s);
    setSessionDetail(null);
    setSessionLoading(true);
    getSessionActivities(s.sessionId)
      .then(({ data: res }) => {
        setSessionDetail(res);
        setSessionLoading(false);
      })
      .catch(() => {
        setSessionLoading(false);
      });
  };

  const totals = data ? data.totals : null;
  const showOwnezzy = Boolean(totals && totals.ownezzyVisits !== undefined);
  const showClicks = isSuper;
  const visitsOf = (d) => (d.ownezzyVisits || 0) + d.storeVisits;
  const visits = totals ? visitsOf(totals) : 0;
  const peakVisits = data ? Math.max(1, ...data.series.map(visitsOf), ...(showClicks ? data.series.map((d) => d.getFreeClicks) : [])) : 1;
  const peakOrders = data ? Math.max(1, ...data.series.map((d) => d.orders)) : 1;
  const storeOptions = data ? data.stores : [];
  const rangeMeta = range === "1h" ? "Past 1 hour" : range === "1d" ? "Today" : `Last ${data?.days || range} days`;

  const tracking = data?.tracking || null;
  const funnel = tracking?.funnel || null;
  const visitors = tracking?.visitors || [];
  const activityFeed = tracking?.activityFeed || [];
  const topLocations = tracking?.topLocations || [];

  const filteredFeed = activityFeed.filter((act) => {
    if (activityFilter === "all") return true;
    if (activityFilter === "clicks") return act.type === "click";
    if (activityFilter === "scrolls") return act.type === "scroll";
    if (activityFilter === "navigations") return act.type === "navigation";
    if (activityFilter === "funnel") return act.type.startsWith("modal_") || act.type.startsWith("exit_intent_") || act.type === "trial_continue_click";
    if (activityFilter === "exits") return act.type === "site_leave" || act.type === "site_exit";
    return true;
  });

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">TRAFFIC AND SALES</p>
          <h1>Analytics</h1>
          <p className="subheading">{isSuper ? "Visitors to ownezzy.com and demo stores, conversion funnel, storefront activity, and sales." : "Visitors to your store websites, and the orders and sales they bring."}</p>
        </div>
        <div className="analytics-controls">
          {(isSuper || storeOptions.length > 1) && (
            <select className="analytics-site-select" value={site} onChange={(e) => setSite(e.target.value)} aria-label="Website">
              <option value="">{isSuper ? "All websites" : "All my stores"}</option>
              {storeOptions.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          )}
          <div className="mode-switch" role="group" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.value}
                className={String(range) === String(r.value) ? "active" : ""}
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {!data && !error && <Loader label="Loading analytics" />}
      {totals && (
        <>
          <div className="stats-grid analytics-stats">
            {showOwnezzy && <Stat label="ownezzy.com visits" value={count(totals.ownezzyVisits)} meta={rangeMeta} icon="◎" />}
            <Stat label="Store website visits" value={count(totals.storeVisits)} meta={isSuper ? "Demo and customer stores" : rangeMeta} icon="▤" />
            <Stat
              label={isSuper ? "Website purchases" : "Orders"}
              value={count(totals.orders)}
              meta={isSuper ? (totals.orders ? `${money(Math.round(totals.sales / totals.orders))} avg sale` : "No purchases yet") : (totals.orders ? `${percent(totals.orders, totals.storeVisits)} of store visits` : "No orders yet")}
              icon="▣"
            />
            <Stat
              label="Sales"
              value={money(totals.sales)}
              meta={isSuper ? "From website purchases" : (totals.orders ? `${money(Math.round(totals.sales / totals.orders))} per order` : "No orders yet")}
              icon="₹"
            />
            {showClicks && <Stat label="“Get it for free” clicks" value={count(totals.getFreeClicks)} meta={visits ? `${percent(totals.getFreeClicks, visits)} of visits` : "No visits yet"} icon="↗" />}
            {isSuper && (
              <Stat
                label="“Continue with 7-day trial” clicks"
                value={count(funnel?.trialContinuedCount || totals?.trialContinueClicks || 0)}
                meta={totals?.getFreeClicks ? `${percent(funnel?.trialContinuedCount || totals?.trialContinueClicks || 0, totals.getFreeClicks)} of “Get free” clicks` : rangeMeta}
                icon="⚡"
              />
            )}
          </div>

          {reAnalyzeToast && (
            <div className="reanalyze-toast-banner" role="status">
              <div className="toast-left">
                <span className="toast-icon">✓</span>
                <div>
                  <strong>{reAnalyzeToast.message}</strong>
                  <span className="toast-time">Updated at {reAnalyzeToast.timestamp}</span>
                </div>
              </div>
              <button
                type="button"
                className="toast-close-btn"
                onClick={() => setReAnalyzeToast(null)}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          )}

          {/* AI User Behavior & CRO Intelligence Hub */}
          {isSuper && tracking?.aiInsights && (
            <AiBehaviorHub
              insights={tracking.aiInsights}
              rangeMeta={rangeMeta}
              site={site}
              onRefresh={handleRefreshAi}
              isRefreshing={isRefreshingAi}
              reAnalyzeToast={reAnalyzeToast}
              onDismissToast={() => setReAnalyzeToast(null)}
            />
          )}

          {/* Super Admin Conversion Funnel */}
          {isSuper && funnel && (
            <section className="panel tab-gap analytics-funnel-panel">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">CONVERSION FUNNEL</span>
                  <h2>“Get It For Free” Modal Funnel</h2>
                  <p>Tracking modal opens, close/cross clicks, plan selections, OTP verification, and domain submissions.</p>
                </div>
              </div>

              <div className="analytics-funnel-stages">
                <div className="funnel-stage">
                  <div className="stage-num">1</div>
                  <div className="stage-content">
                    <span className="stage-label">Modal Opened</span>
                    <strong className="stage-val">{count(funnel.modalOpens)}</strong>
                    <small className="stage-sub">Total impressions</small>
                  </div>
                </div>

                <div className="funnel-stage">
                  <div className="stage-num">2</div>
                  <div className="stage-content">
                    <span className="stage-label">Cross (Close) Clicked</span>
                    <strong className="stage-val close-val">{count(funnel.modalCloses)}</strong>
                    <small className="stage-sub">{percent(funnel.modalCloses, funnel.modalOpens)} modal close rate</small>
                  </div>
                </div>

                <div className="funnel-stage">
                  <div className="stage-num">3</div>
                  <div className="stage-content">
                    <span className="stage-label">Plan Selected</span>
                    <strong className="stage-val">{count(funnel.planSelectionsCount)}</strong>
                    <div className="funnel-breakdown">
                      {funnel.planBreakdown?.length > 0 ? (
                        funnel.planBreakdown.map((p) => (
                          <span key={p.plan} className="funnel-pill">
                            <b>{p.name}:</b> {count(p.count)}
                          </span>
                        ))
                      ) : (
                        <small className="stage-sub">No plans selected</small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="funnel-stage highlight-trial-stage">
                  <div className="stage-num">⚡</div>
                  <div className="stage-content">
                    <span className="stage-label">“Continue with 7-day trial” Clicked</span>
                    <strong className="stage-val highlight-val">{count(funnel.trialContinuedCount)}</strong>
                    <small className="stage-sub">{percent(funnel.trialContinuedCount, funnel.modalOpens)} of modal opens</small>
                  </div>
                </div>

                <div className="funnel-stage">
                  <div className="stage-num">4</div>
                  <div className="stage-content">
                    <span className="stage-label">OTP Verified</span>
                    <strong className="stage-val">{count(funnel.otpVerifiedCount)}</strong>
                    <small className="stage-sub">{percent(funnel.otpVerifiedCount, funnel.planSelectionsCount)} of plan picks</small>
                  </div>
                </div>

                <div className="funnel-stage">
                  <div className="stage-num">5</div>
                  <div className="stage-content">
                    <span className="stage-label">Domain Submitted</span>
                    <strong className="stage-val success-val">{count(funnel.domainSubmissions?.total)}</strong>
                    <div className="funnel-breakdown">
                      <span className="funnel-pill">
                        <b>Subdomain:</b> {count(funnel.domainSubmissions?.subdomainCount)}
                      </span>
                      <span className="funnel-pill">
                        <b>GoDaddy:</b> {count(funnel.domainSubmissions?.mainDomainSuggestedCount)}
                      </span>
                      <span className="funnel-pill">
                        <b>Own Domain:</b> {count(funnel.domainSubmissions?.mainDomainSelfCount)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="funnel-stage">
                  <div className="stage-num">👀</div>
                  <div className="stage-content">
                    <span className="stage-label">"Not Your Business Type?" Prompt</span>
                    <strong className="stage-val info-val">{count(funnel.exitPrompt?.shown)}</strong>
                    <div className="funnel-breakdown">
                      <span className="funnel-pill">
                        <b>Clicked Demos:</b> {count(funnel.exitPrompt?.clicked)} ({percent(funnel.exitPrompt?.clicked, funnel.exitPrompt?.shown)})
                      </span>
                      <span className="funnel-pill">
                        <b>Dismissed:</b> {count(funnel.exitPrompt?.dismissed)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {funnel.domainSubmissions?.recent?.length > 0 && (
                <div className="funnel-recent-domains">
                  <div className="funnel-recent-title">
                    <b>Recent Domain Submissions</b>
                    <span>({count(funnel.domainSubmissions.recent.length)} recent)</span>
                  </div>
                  <DataTable>
                    <thead>
                      <tr>
                        <th>DOMAIN / STORE NAME</th>
                        <th>TYPE</th>
                        <th>PRICE</th>
                        <th>STORE</th>
                        <th>LOCATION</th>
                        <th>TIME</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.domainSubmissions.recent.map((d, idx) => (
                        <tr key={idx}>
                          <td><b>{d.domain}</b></td>
                          <td>
                            <span className={`status-pill ${d.domainType === 'main' ? (d.isSelfDomain ? 'pill-purple' : 'pill-green') : 'pill-blue'}`}>
                              {d.domainType === 'main' ? (d.isSelfDomain ? 'Own Domain' : 'GoDaddy Domain') : 'Subdomain'}
                            </span>
                          </td>
                          <td>{d.price ? money(d.price) : 'Free'}</td>
                          <td>{d.site}</td>
                          <td>{d.city ? `${d.city}, ` : ''}{d.country || '–'}</td>
                          <td><small className="muted-note">{timeAgo(d.createdAt)}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </div>
              )}
            </section>
          )}

          {/* Super Admin Visitor IPs and Geographic Locations */}
          {isSuper && tracking && (
            <section className="panel table-panel tab-gap">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">AUDIENCE &amp; LOCATIONS</span>
                  <h2>Visitor Locations &amp; IPs</h2>
                  <p>Tracked IP addresses, geographic location, device type, and storefront activity.</p>
                </div>
              </div>

              {topLocations.length > 0 && (
                <div className="analytics-location-chips">
                  <span className="loc-label">Top locations:</span>
                  {topLocations.map((loc, idx) => (
                    <span key={idx} className="loc-chip">
                      📍 {loc.city ? `${loc.city}, ` : ''}{loc.country} <b>({count(loc.count)})</b>
                    </span>
                  ))}
                </div>
              )}

              <DataTable>
                <thead>
                  <tr>
                    <th>IP ADDRESS</th>
                    <th>LOCATION</th>
                    <th>DEVICE</th>
                    <th>WEBSITE</th>
                    <th>MODAL FUNNEL</th>
                    <th>ACTIVITIES</th>
                    <th>TIME SPENT</th>
                    <th>LAST ACTIVE</th>
                    <th>DETAILS</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v) => (
                    <tr key={v.sessionId}>
                      <td>
                        <code className="ip-badge">{v.ip || '–'}</code>
                      </td>
                      <td>
                        <b>{v.city ? `${v.city}, ` : ''}{v.country || 'Unknown'}</b>
                        {v.region && <small className="muted-note"> · {v.region}</small>}
                      </td>
                      <td>
                        <span className={`device-tag ${v.device?.toLowerCase()}`}>
                          {v.device === 'Mobile' ? '📱 Mobile' : v.device === 'Tablet' ? '📟 Tablet' : '💻 Desktop'}
                        </span>
                      </td>
                      <td><b>{v.site}</b></td>
                      <td>
                        {v.funnel?.domainSubmitted ? (
                          <span className="funnel-badge success">🌐 {v.funnel.domainSubmitted}</span>
                        ) : v.funnel?.otpVerified ? (
                          <span className="funnel-badge success">✓ OTP Verified</span>
                        ) : (v.funnel?.trialContinued || v.funnel?.trialContinuedCount > 0) ? (
                          <span className="funnel-badge highlight-trial">⚡ 7-Day Trial{v.funnel.trialContinuedCount > 1 ? ` (${v.funnel.trialContinuedCount}x)` : ''}</span>
                        ) : v.funnel?.planSelected ? (
                          <span className="funnel-badge info">Plan: {v.funnel.planSelected}</span>
                        ) : v.funnel?.modalClosedCount > 0 ? (
                          <span className="funnel-badge warning">Closed modal ({v.funnel.modalClosedCount}x)</span>
                        ) : (
                          <span className="funnel-badge neutral">Browsing</span>
                        )}
                      </td>
                      <td>
                        <span className="activity-summary">
                          <b>{count(v.activityCounts?.total)}</b> actions
                          <small className="muted-note"> ({v.activityCounts?.clicks || 0} clicks · {v.activityCounts?.scrolls || 0} scrolls · {v.activityCounts?.navigations || 0} nav)</small>
                        </span>
                      </td>
                      <td>
                        <span className="duration-tag">
                          ⏱ {formatDuration(v.durationSeconds)}
                        </span>
                      </td>
                      <td><small className="muted-note">{timeAgo(v.lastSeen)}</small></td>
                      <td>
                        <button
                          type="button"
                          className="table-action-btn"
                          onClick={() => openSessionDetail(v)}
                        >
                          View Activity
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!visitors.length && (
                    <tr>
                      <td colSpan="9" className="empty-row">No visitor sessions recorded yet in this period.</td>
                    </tr>
                  )}
                </tbody>
              </DataTable>
            </section>
          )}

          {/* Super Admin Live Store Activity Stream */}
          {isSuper && tracking && (
            <section className="panel tab-gap analytics-activity-panel">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">DEMO STORE BROWSING</span>
                  <h2>Live Store Activity Stream</h2>
                  <p>Every click, scroll milestone, and navigation recorded while browsing the website.</p>
                </div>
                <div className="activity-filter-tabs">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'clicks', label: 'Clicks' },
                    { key: 'scrolls', label: 'Scrolls' },
                    { key: 'navigations', label: 'Navigations' },
                    { key: 'funnel', label: 'Modal Funnel' },
                    { key: 'exits', label: 'Left / Closed' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`activity-tab-btn ${activityFilter === tab.key ? 'active' : ''}`}
                      onClick={() => setActivityFilter(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="activity-feed-list">
                {filteredFeed.map((act) => {
                  const isClick = act.type === 'click';
                  const isScroll = act.type === 'scroll';
                  const isNav = act.type === 'navigation';
                  const isExit = act.type === 'site_leave' || act.type === 'site_exit';
                  const isExitPrompt = act.type.startsWith('exit_intent_');
                  return (
                    <div key={act.id} className={`activity-feed-item ${act.type} ${isExit ? 'is-exit' : ''}`}>
                      <div className="activity-feed-badge">
                        <span className={`act-type-tag ${act.type}`}>
                          {isClick ? 'CLICK' : isScroll ? 'SCROLL' : isNav ? 'NAV' : act.type === 'trial_continue_click' ? '7-DAY TRIAL' : isExit ? 'LEFT / CLOSED' : isExitPrompt ? 'PROMPT' : 'MODAL'}
                        </span>
                      </div>
                      <div className="activity-feed-main">
                        <div className="activity-feed-title">
                          <b>{act.target}</b>
                          {act.meta?.timeSpent && (
                            <span className="act-duration-badge">⏱ {act.meta.timeSpent}</span>
                          )}
                          {act.path && <code className="activity-path">{act.path}</code>}
                        </div>
                        <div className="activity-feed-meta">
                          <span>IP: {act.ip || '–'}</span>
                          {act.city && <span>📍 {act.city}, {act.country}</span>}
                          <span>Site: <b>{act.site}</b></span>
                          <span className="activity-time">{timeAgo(act.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!filteredFeed.length && (
                  <div className="empty-activity-note">
                    No activity records found matching this filter in this period.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Visits Day-by-Day Chart */}
          <section className="panel analytics-chart-panel tab-gap">
            <div className="panel-heading">
              <div><h2>Visits {range === "1h" ? "in past hour" : range === "1d" ? "today" : "day by day"}</h2><p>{showOwnezzy ? "Stacked by site" : "Store visits"}{showClicks ? ", clicks beside them." : "."}</p></div>
              <div className="analytics-legend">
                {showOwnezzy && <span><i className="a-ownezzy" />ownezzy.com</span>}<span><i className="a-store" />Stores</span>{showClicks && <span><i className="a-click" />Get it for free</span>}
              </div>
            </div>
            <div className="analytics-bars" style={{ "--cols": data.series.length }}>
              {data.series.map((d) => (
                <div className="analytics-day" key={d.day} title={`${shortDay(d.day)}: ${showOwnezzy ? `${d.ownezzyVisits} ownezzy.com, ` : ""}${d.storeVisits} store visits${showClicks ? `, ${d.getFreeClicks} clicks` : ""}`}>
                  <div className="analytics-stack">
                    <span className="a-visit">
                      <i className="a-store" style={{ height: `${(d.storeVisits / peakVisits) * 100}%` }} />
                      {showOwnezzy && <i className="a-ownezzy" style={{ height: `${(d.ownezzyVisits / peakVisits) * 100}%` }} />}
                    </span>
                    {showClicks && <span className="a-clicks"><i className="a-click" style={{ height: `${(d.getFreeClicks / peakVisits) * 100}%` }} /></span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="analytics-axis">
              <span>{data.series[0] ? shortDay(data.series[0].day) : ""}</span><span>Peak {peakVisits}</span><span>{data.series[data.series.length - 1] ? shortDay(data.series[data.series.length - 1].day) : ""}</span>
            </div>
          </section>

          {/* Purchases / Orders Day-by-Day Chart */}
          <section className="panel analytics-chart-panel tab-gap">
            <div className="panel-heading">
              <div>
                <h2>{isSuper ? "Website purchases" : "Orders"} {range === "1h" ? "in past hour" : range === "1d" ? "today" : "day by day"}</h2>
                <p>{isSuper ? "Paid website and subscription purchases." : "Placed or paid orders; cancelled, refunded and unpaid ones are left out."}</p>
              </div>
              <div className="analytics-legend"><span><i className="a-order" />{isSuper ? "Purchases" : "Orders"}</span></div>
            </div>
            <div className="analytics-bars" style={{ "--cols": data.series.length }}>
              {data.series.map((d) => (
                <div className="analytics-day" key={d.day} title={`${shortDay(d.day)}: ${d.orders} ${isSuper ? "purchases" : "orders"}, ${money(d.sales)}`}>
                  <div className="analytics-stack">
                    <span className="a-visit"><i className="a-order" style={{ height: `${(d.orders / peakOrders) * 100}%` }} /></span>
                  </div>
                </div>
              ))}
            </div>
            <div className="analytics-axis">
              <span>{data.series[0] ? shortDay(data.series[0].day) : ""}</span><span>Peak {peakOrders}</span><span>{data.series[data.series.length - 1] ? shortDay(data.series[data.series.length - 1].day) : ""}</span>
            </div>
          </section>

          {/* By Website Table */}
          <section className="panel table-panel tab-gap">
            <div className="panel-heading"><div><h2>By website</h2><p>Busiest first.</p></div></div>
            <DataTable>
              <thead><tr><th>WEBSITE</th>{isSuper && <th>TYPE</th>}<th>VISITS</th><th>{isSuper ? "PURCHASES" : "ORDERS"}</th><th>SALES</th>{showClicks && <th>GET IT FOR FREE</th>}{isSuper && <th>7-DAY TRIAL CLICKS</th>}</tr></thead>
              <tbody>
                {data.sites.map((s) => (
                  <tr key={s.site}>
                    <td><b>{s.name}</b>{s.site !== "ownezzy" && <small className="muted-note"> · {s.site}</small>}</td>
                    {isSuper && <td>{KIND_LABELS[s.kind]}</td>}
                    <td>{count(s.visits)}</td><td>{count(s.orders)}</td><td>{money(s.sales)}</td>
                    {showClicks && <td>{count(s.getFreeClicks)}</td>}
                    {isSuper && <td><b>{count(s.trialContinueClicks)}</b></td>}
                  </tr>
                ))}
                {!data.sites.length && <tr><td colSpan={isSuper ? "7" : "6"} className="empty-row">No visits or orders yet in this period.</td></tr>}
              </tbody>
            </DataTable>
          </section>
        </>
      )}

      {/* Session Drilldown Modal */}
      {selectedSession && (
        <Modal
          title={`Visitor Activity: ${selectedSession.ip || 'Session'}`}
          eyebrow="SESSION DETAILS"
          description={`Browsing activity on ${selectedSession.site} · Location: ${selectedSession.city ? `${selectedSession.city}, ` : ''}${selectedSession.country || 'Unknown'}`}
          onClose={() => {
            setSelectedSession(null);
            setSessionDetail(null);
          }}
          wide
        >
          <div className="session-modal-content">
            <div className="session-meta-grid">
              <div>
                <span>IP Address</span>
                <b>{selectedSession.ip || '–'}</b>
              </div>
              <div>
                <span>Location</span>
                <b>{selectedSession.city ? `${selectedSession.city}, ` : ''}{selectedSession.country || '–'}</b>
              </div>
              <div>
                <span>Device</span>
                <b>{selectedSession.device || 'Desktop'}</b>
              </div>
              <div>
                <span>Store</span>
                <b>{selectedSession.site}</b>
              </div>
              <div>
                <span>Total Actions</span>
                <b>{selectedSession.activityCounts?.total || 0}</b>
              </div>
              <div>
                <span>Total Time Spent</span>
                <b className="duration-highlight">⏱ {formatDuration(sessionDetail?.session?.durationSeconds || selectedSession.durationSeconds)}</b>
              </div>
              <div>
                <span>Last Active</span>
                <b>{timeAgo(selectedSession.lastSeen)}</b>
              </div>
            </div>

            <h3 className="session-history-title">Activity Trail</h3>
            {sessionLoading && <Loader label="Loading visitor activities..." />}
            {!sessionLoading && sessionDetail && (
              <div className="session-activities-scroll">
                {sessionDetail.activities?.map((a) => {
                  const isExit = a.type === 'site_leave' || a.type === 'site_exit';
                  return (
                    <div key={a._id} className={`session-act-row ${isExit ? 'is-exit' : ''}`}>
                      <span className={`act-type-tag ${a.type}`}>
                        {isExit ? 'LEFT / CLOSED' : a.type === 'trial_continue_click' ? '7-DAY TRIAL' : a.type.replace('modal_', '').toUpperCase()}
                      </span>
                      <div className="session-act-body">
                        <b>{a.target}</b>
                        {a.meta?.timeSpent && (
                          <span className="act-duration-badge">⏱ Total time spent: {a.meta.timeSpent}</span>
                        )}
                        {a.path && <small className="muted-note">Path: {a.path}</small>}
                      </div>
                      <time className="session-act-time">{timeAgo(a.createdAt)}</time>
                    </div>
                  );
                })}
                {!sessionDetail.activities?.length && (
                  <p className="empty-row">No specific activities logged for this session.</p>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

// Super Admins switch between traffic & sales and the Admin Tracker (what store admins do in the panel)
const VIEW_KEY = "oz_analytics_view";
export default function AnalyticsTab(props) {
  const [view, setView] = useState(() => sessionStorage.getItem(VIEW_KEY) || "traffic");
  if (props.user.role !== "superadmin") return <TrafficAnalytics {...props} />;
  const choose = (next) => { sessionStorage.setItem(VIEW_KEY, next); setView(next); };
  return (
    <>
      <div className="mode-switch analytics-views" role="tablist" aria-label="Analytics view">
        <button role="tab" aria-selected={view === "traffic"} className={view === "traffic" ? "active" : ""} onClick={() => choose("traffic")}>📈 Traffic & sales</button>
        <button role="tab" aria-selected={view === "tracker"} className={view === "tracker" ? "active" : ""} onClick={() => choose("tracker")}>🕵️ Admin Tracker</button>
      </div>
      {view === "tracker" ? (
        <>
          <div className="page-heading">
            <div>
              <p className="eyebrow">STORE ADMIN ACTIVITY</p>
              <h1>Admin Tracker</h1>
              <p className="subheading">Sign-ins, sessions, time spent, tabs used and every change made by a store's owner and staff.</p>
            </div>
          </div>
          <AdminTracker />
        </>
      ) : <TrafficAnalytics {...props} />}
    </>
  );
}
